// The athlete-facing surface (see `features/athlete-access.md`) — what an athlete
// or a guardian sees after signing in, instead of the club-admin layout. Three
// tabs over one profile at a time:
//
//   * My profile   — identity, bio, photos, sports, teams, training; editable
//                    down to photos + bio via `MyProfileEditDialog`.
//   * My clubs     — the clubs they train at, each with their attendance
//                    frequency and next session; opening one shows the club page
//                    (`MyClubsList` → `MyClubView`; see `features/athlete-clubs.md`).
//   * Access       — who else can see this profile, a guardian's "give them their
//                    own login" action, and revoking.
//
// A guardian with several children switches between profiles with the selector in
// the header; an athlete with one profile never sees it. Mobile-first, since this
// is a phone surface far more than a desktop one.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import SportsIcon from '@mui/icons-material/Sports';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import GroupsIcon from '@mui/icons-material/Groups';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import type {
  AthleteClubSummary,
  AthleteClubView,
  AthleteLinkView,
  AthleteProfile,
} from '../../domain';
import { groupSchedulePatterns, schedulePatternLabel } from '../../domain';
import { fetchMyAthleteProfile, fetchMyClub, fetchMyClubs } from '../../data/apiClient';
import { useAuth } from '../../state/AuthContext';
import { useTranslation } from '../../state/I18nContext';
import { BrandLogo } from '../BrandLogo';
import { AccountMenu } from '../AccountMenu';
import { LanguageSelector } from '../LanguageSelector';
import { PhotoGallery } from '../PhotoGallery';
import { MyProfileEditDialog } from './MyProfileEditDialog';
import { MyClubsList } from './MyClubsList';
import { MyClubView } from './MyClubView';
import { MyAccessSection } from './MyAccessSection';

type AthleteTab = 'profile' | 'club' | 'access';

interface AthleteHomeViewProps {
  /** The profiles this account reaches; never empty (the app only forks when it is not). */
  links: AthleteLinkView[];
  /** Leave the athlete surface for the staff layout — only offered to staff. */
  onFullSite?: () => void;
}

export function AthleteHomeView({ links, onFullSite }: AthleteHomeViewProps) {
  const { refreshMe } = useAuth();
  const { t, localeCode } = useTranslation();

  const [personalNumber, setPersonalNumber] = useState(links[0].personalNumber);
  const [tab, setTab] = useState<AthleteTab>('profile');
  const [profile, setProfile] = useState<AthleteProfile | null>(null);
  const [clubs, setClubs] = useState<AthleteClubSummary[]>([]);
  // The club whose page is open within the My clubs tab, loaded on demand.
  const [openClub, setOpenClub] = useState<AthleteClubView | null>(null);
  const [clubLoading, setClubLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // The selected profile may vanish (a guardian's grant revoked mid-session);
  // fall back to whatever is still reachable.
  const active = useMemo(
    () => links.find((link) => link.personalNumber === personalNumber) ?? links[0],
    [links, personalNumber],
  );

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextProfile, nextClubs] = await Promise.all([
        fetchMyAthleteProfile(active.personalNumber),
        fetchMyClubs(active.personalNumber),
      ]);
      setProfile(nextProfile);
      setClubs(nextClubs);
      // Switching profile abandons any open club page — it belonged to the
      // previous athlete.
      setOpenClub(null);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : tRef.current('athlete.profile.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [active.personalNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(localeCode, { dateStyle: 'medium', timeZone: 'UTC' }),
    [localeCode],
  );

  /** Open one club's page — the extended member view of it. */
  const openClubPage = async (companyId: number) => {
    setClubLoading(true);
    setError(null);
    try {
      setOpenClub(await fetchMyClub(active.personalNumber, companyId));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('athlete.club.loadFailed'));
    } finally {
      setClubLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <AppBar position="sticky" color="default" enableColorOnDark>
        <Toolbar sx={{ gap: 1.5 }}>
          <BrandLogo />
          <Typography variant="h6" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            {t('athlete.appTitle')}
          </Typography>
          <LanguageSelector />
          {/* Same account menu as the staff surfaces: settings and sign-out are
              found in the same place whichever app a person lands in. */}
          <AccountMenu onFullSite={onFullSite} fullSiteLabel={t('athlete.fullSite')} />
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* A guardian with more than one child picks whose profile they are on. */}
        {links.length > 1 && (
          <TextField
            select
            fullWidth
            size="small"
            label={t('athlete.switcher.label')}
            value={active.personalNumber}
            onChange={(e) => {
              setPersonalNumber(e.target.value);
              setTab('profile');
            }}
            sx={{ mb: 3 }}
          >
            {links.map((link) => (
              <MenuItem key={link.personalNumber} value={link.personalNumber}>
                {link.fullName}
                {link.relation === 'GUARDIAN' && ` · ${t('athlete.switcher.asGuardian')}`}
              </MenuItem>
            ))}
          </TextField>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Header: who this profile belongs to, and how the caller relates to it. */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Avatar
            src={profile?.photoUrls[0] ?? undefined}
            alt={active.fullName}
            sx={{ width: 72, height: 72 }}
          >
            {profile?.isCoach ? (
              <SportsIcon fontSize="large" />
            ) : (
              <DirectionsRunIcon fontSize="large" />
            )}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h5" noWrap title={active.fullName}>
              {active.fullName}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                color={active.relation === 'GUARDIAN' ? 'secondary' : 'primary'}
                variant="outlined"
                label={t(`athlete.relation.${active.relation}`)}
              />
              {profile?.club && (
                <Chip size="small" variant="outlined" label={profile.club.name} />
              )}
            </Stack>
          </Box>
          {profile && active.canEdit && (
            <Button size="small" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
              {t('athlete.profile.edit.action')}
            </Button>
          )}
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, next: AthleteTab) => setTab(next)}
          variant="fullWidth"
          sx={{ mb: 3 }}
        >
          <Tab value="profile" label={t('athlete.tabs.profile')} />
          <Tab value="club" label={t('athlete.tabs.clubs')} />
          <Tab value="access" label={t('athlete.tabs.access')} />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !profile ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('athlete.profile.notFound')}
            </Typography>
          </Paper>
        ) : tab === 'profile' ? (
          <Stack spacing={3}>
            {/* Basic information. The identity fields are read-only here: they are
                club-managed (see the feature doc), which the hint says plainly. */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('athlete.profile.basicInfo')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`admin.athletes.gender.${profile.gender}`)}
                {' · '}
                {t('admin.athletes.born', {
                  date: dateFormat.format(new Date(`${profile.dateOfBirth}T00:00:00Z`)),
                })}
                {profile.age !== null &&
                  ` · ${t('admin.athletes.age', { count: profile.age })}`}
              </Typography>
              {profile.coachDescription ? (
                <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
                  {profile.coachDescription}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {t('athlete.profile.noBio')}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                {t('athlete.profile.clubManagedNote')}
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('athlete.profile.photos')}
              </Typography>
              <PhotoGallery
                urls={profile.photoUrls}
                alt={profile.fullName}
                emptyLabel={t('athlete.profile.noPhotos')}
                labels={{
                  close: t('gallery.close'),
                  download: t('gallery.download'),
                  previous: t('gallery.previous'),
                  next: t('gallery.next'),
                }}
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('athlete.profile.sports')}
              </Typography>
              {profile.sports.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('athlete.profile.noSports')}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {profile.sports.map((sport) => (
                    <Stack
                      key={sport.sportId}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Chip color="info" variant="outlined" label={sport.sportName} />
                      {Object.entries(sport.attributes).map(([key, value]) => (
                        <Chip
                          key={key}
                          size="small"
                          variant="outlined"
                          label={`${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`}
                        />
                      ))}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('athlete.profile.teams')}
              </Typography>
              {profile.teams.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('athlete.profile.noTeams')}
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {profile.teams.map((team) => (
                    <Stack key={team.teamId} direction="row" spacing={1.5} alignItems="center">
                      <GroupsIcon color="disabled" />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" noWrap>
                          {team.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Chip size="small" variant="outlined" label={team.sportName} />
                          {team.teamRole && (
                            <Chip size="small" variant="outlined" label={team.teamRole} />
                          )}
                          {team.tier && <Chip size="small" variant="outlined" label={team.tier} />}
                        </Stack>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('athlete.profile.training')}
              </Typography>
              {profile.training.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('athlete.profile.noTraining')}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {profile.training.map((entry) => (
                    <Box key={`${entry.role}-${entry.trainingClassId}`}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <FitnessCenterIcon color="disabled" />
                        <Typography variant="subtitle1" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                          {entry.name}
                        </Typography>
                        <Chip
                          size="small"
                          color={entry.role === 'COACH' ? 'secondary' : 'default'}
                          variant="outlined"
                          label={t(`admin.athleteProfile.classRole.${entry.role}`)}
                        />
                      </Stack>
                      {entry.schedule.length > 0 && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mt: 0.5, ml: 4.5 }}
                        >
                          {groupSchedulePatterns(entry.schedule).map((pattern, i) => (
                            <Chip
                              key={i}
                              size="small"
                              variant="outlined"
                              label={schedulePatternLabel(pattern, (day) =>
                                t(`admin.trainingClasses.weekdays.${day}`),
                              )}
                            />
                          ))}
                        </Stack>
                      )}
                      {entry.role === 'ATHLETE' && (
                        <Box sx={{ ml: 4.5, mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('athlete.profile.attendance', {
                              present: entry.attendance.present,
                              marked: entry.attendance.marked,
                            })}
                            {entry.attendance.marked > 0 &&
                              ` · ${Math.round(entry.attendance.rate * 100)}%`}
                          </Typography>
                          {entry.attendance.marked > 0 && (
                            <LinearProgress
                              variant="determinate"
                              value={entry.attendance.rate * 100}
                              sx={{ mt: 0.5, maxWidth: 240, borderRadius: 1 }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Stack>
        ) : tab === 'club' ? (
          clubLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : openClub ? (
            <MyClubView club={openClub} onBack={() => setOpenClub(null)} />
          ) : (
            <MyClubsList clubs={clubs} onOpen={(companyId) => void openClubPage(companyId)} />
          )
        ) : (
          <MyAccessSection
            link={active}
            onChanged={() => {
              // A revoked or newly granted link changes the caller's own access
              // picture, which the app routes on.
              void refreshMe();
            }}
          />
        )}
      </Container>

      {profile && (
        <MyProfileEditDialog
          open={editOpen}
          profile={profile}
          onClose={() => setEditOpen(false)}
          onSaved={(saved) => {
            setProfile(saved);
            setEditOpen(false);
          }}
        />
      )}
    </Box>
  );
}
